import { useEffect, useState } from "react";

/**
 * TEMPORARY (owner 2026-09-18): the switch for the APK's remote debugging server, which
 * lets a laptop on the same Wi-Fi read logs and drive a TV that has no adb.
 * Native side: native/android/.../debug/RemoteDebugPlugin.java. Renders nothing
 * outside the Android app.
 */

interface RemoteDebugState {
  enabled: boolean;
  running: boolean;
  port: number;
  pin: string;
  address: string;
  error?: string;
}

interface CapacitorLike {
  isNativePlatform?: () => boolean;
  PluginHeaders?: Array<{ name: string }>;
  nativePromise?: (plugin: string, method: string, options: Record<string, unknown>) => Promise<unknown>;
}

const PLUGIN = "RemoteDebug";

function call(method: string, options: Record<string, unknown> = {}): Promise<RemoteDebugState> | null {
  const cap = (globalThis as { Capacitor?: CapacitorLike }).Capacitor;
  if (cap?.isNativePlatform?.() !== true || typeof cap.nativePromise !== "function") return null;
  if (!cap.PluginHeaders?.some((header) => header.name === PLUGIN)) return null;
  return cap.nativePromise(PLUGIN, method, options) as Promise<RemoteDebugState>;
}

/** Where test builds are published. Editable here — nothing is stored in the app. */
const DEFAULT_UPDATE_URL = "https://sliquetv.online/sliqueplayer";

export function RemoteDebugCard() {
  const [state, setState] = useState<RemoteDebugState | null>(null);
  const [busy, setBusy] = useState(false);
  const [updateUrl, setUpdateUrl] = useState(DEFAULT_UPDATE_URL);
  const [updating, setUpdating] = useState(false);
  const [updateNote, setUpdateNote] = useState<string | null>(null);

  useEffect(() => {
    void call("getState")?.then(setState).catch(() => setState(null));
  }, []);

  if (!state) return null;

  const toggle = async () => {
    setBusy(true);
    try {
      const next = await call("setEnabled", { enabled: !state.enabled });
      if (next) setState(next);
    } finally {
      setBusy(false);
    }
  };

  const installUpdate = async () => {
    setUpdating(true);
    setUpdateNote("Downloading…");
    try {
      const result = (await call("update", { url: updateUrl })) as unknown as { bytes?: number } | null;
      const mb = result?.bytes ? Math.round(result.bytes / 1048576) : null;
      setUpdateNote(mb === null ? "Downloaded. Android will ask to install it." : `Downloaded ${mb} MB. Android will ask to install it.`);
    } catch (error) {
      setUpdateNote(error instanceof Error ? error.message : "Download failed.");
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="settings-card">
      <h2 className="settings-card-title">Remote debugging</h2>
      <p className="settings-card-desc">
        Lets a computer on this Wi-Fi read the app&apos;s logs and control it, for fixing TV problems.
        Temporary. Leave it off unless you are debugging.
      </p>
      <div className="settings-row">
        <span className="settings-row-label">Debugger</span>
        <button
          type="button"
          className={`ui-btn ${state.enabled ? "ui-btn--primary" : ""}`}
          onClick={() => void toggle()}
          disabled={busy}
          aria-pressed={state.enabled}
        >
          {state.enabled ? "On" : "Off"}
        </button>
      </div>
      {state.enabled && (
        <>
          <div className="settings-row">
            <span className="settings-row-label">Address</span>
            <span className="settings-row-value" style={{ overflowWrap: "anywhere" }}>
              {state.address ? `${state.address}:${state.port}` : "No Wi-Fi address"}
            </span>
          </div>
          <div className="settings-row">
            <span className="settings-row-label">PIN</span>
            <span className="settings-row-value">{state.pin}</span>
          </div>
          {!state.running && (
            <div className="settings-row">
              <span className="settings-row-hint">
                Not listening{state.error ? `: ${state.error}` : ""}. Turn it off and on again.
              </span>
            </div>
          )}
          {/* Update without the Downloader app: fetch the published build and
              let Android install it. Testing only — it lives with remote
              debugging and goes when that goes. */}
          <div className="settings-row">
            <span className="settings-row-label">Update from</span>
            <input
              className="settings-input"
              type="url"
              value={updateUrl}
              onChange={(event) => setUpdateUrl(event.target.value)}
              aria-label="Build URL"
              spellCheck={false}
            />
          </div>
          <div className="settings-row">
            <span className="settings-row-label">Install build</span>
            <button type="button" className="ui-btn" onClick={() => void installUpdate()} disabled={updating || !updateUrl}>
              {updating ? "Downloading…" : "Download & install"}
            </button>
          </div>
          {updateNote && (
            <div className="settings-row">
              <span className="settings-row-hint">{updateNote}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
